import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Config } from 'src/app/config';
import { Logger } from 'src/app/core/logger';
import { FieldOptionModel } from 'src/app/models/widgets/field-option.model';
import { WidgetsCheckboxModel } from 'src/app/models/widgets/widgets-checkbox.model';
import { getKeyFromText } from 'src/app/services/shared/camelCase';
import { SharedConstants } from 'src/app/shared/constants/shared-constant';
import { MetadataTemplateService } from 'src/app/services/metadata/metadata-template.service';

@Component({
  selector: 'app-metadata-checkbox',
  templateUrl: './metadata-checkbox.component.html',
  styleUrls: ['./metadata-checkbox.component.scss']
})
export class MetadataCheckboxComponent implements OnInit {
  @Input() data: WidgetsCheckboxModel = new WidgetsCheckboxModel();
  template?: string;
  nextValue: number = 0;
  errorMessage: string = "";
  headerLength: number = SharedConstants.defaultHeader;
  form: FormGroup;
  commentFieldGroup: any= [];

  constructor(
    private fb: FormBuilder,
    public cd: ChangeDetectorRef,
    private logger: Logger,
    private templateService: MetadataTemplateService
    ) {
      this.form = this.fb.group({
        label: [this.data.label, Validators.maxLength(this.headerLength)],
        description: [this.data.description, Validators.maxLength(this.headerLength)],       
        options: this.fb.array([]),
        commentKey: ['', Validators.maxLength(30)],
        commentFieldOn: [null, [Validators.required]],
        commentFieldOnOptional: [false, [Validators.required]]
      });
    }

  ngOnInit(): void {
    this.template = this.data.template;
    this.form = this.fb.group({
      label: [this.data.label, Validators.maxLength(this.headerLength)],
      description: [this.data.description, Validators.maxLength(this.headerLength)],
      options: this.fb.array([]),
      commentKey: [this.data.commentKey, Validators.maxLength(30)],
      commentFieldOn: [null, [Validators.required]],
      commentFieldOnOptional: [false, [Validators.required]]
    });

    const options = this.data.options;
    // If no options provided, add a single enabled option(setInitialValues())
    if (!options || options.length === 0) {
      this.setInitialValues();
    }

    options?.forEach((option, ind) => {
      const add = this.form.get('options') as FormArray;

      if (option.value && option.label) {
        // Formly > V5.5 - Uses {value, label}
        add.push(this.fb.group({
          key: [option.label, Validators.maxLength(SharedConstants.defaultOtherLabelLength)],
          value: [option.value ? option.value : ind]
        }));
      } else if (option.key) {
        // Formly < V5.5 - Uses {value, key}
        add.push(this.fb.group({
          key: [option.key, Validators.maxLength(SharedConstants.defaultOtherLabelLength)],
          value: [option.value ? option.value : getKeyFromText(option.key)]
        }));
      }
    });
    this.nextValue = options? options.length : 0;

    // Initial value
    this.form.controls['commentKey'].setValue(this.data.commentKey ? this.data.commentKey : null);
    this.form.controls['commentFieldOnOptional'].setValue(this.data.commentFieldOnOptional);
    this.form.controls['commentFieldOn'].setValue(this.data.commentFieldOn ? this.data.commentFieldOn : null);
    this.initCommentField();    
  }

  setInitialValues() {
    const add = this.form.get('options') as FormArray;
    add.push(this.fb.group({
      value: ['enabled', [Validators.max(3000), Validators.required]],
      key: ['Enabled', [Validators.maxLength(SharedConstants.defaultOtherLabelLength), Validators.required]]
    }));
    this.nextValue = 1;
    // Save option values
    this.onSaveOptions(this.nextValue);
  }

  /**
   * Only assign a default otherMaxLength if other feature enabled
   */
  setDefaultMaxLength() {
    if (this.data.otherFlag) {
      this.data['otherMaxLength'] = SharedConstants.defaultOtherLabelLength;
   } else {
    delete this.data['otherMaxLength'];
   }
  }

  onAddNewOption() {
    const add = this.form.get('options') as FormArray;
    add.push(this.fb.group({
      key: ['', Validators.maxLength(SharedConstants.defaultOtherLabelLength)],
      value: [Config.CONSTANTS.optionKeyDefaultPrefix+this.nextValue++]
    }));
  }

  onAddNewOptionWithKey(keyName) {
    const add = this.form.get('options') as FormArray;
    add.push(this.fb.group({
      key: [keyName, Validators.maxLength(SharedConstants.defaultOtherLabelLength)],
      value: [Config.CONSTANTS.optionKeyDefaultPrefix+this.nextValue++]
    }));
  }

  onDeleteOption(idx: number): void {
    const add = this.form.get('options') as FormArray;
    // Logic for nextValue
    const deletedValue: FieldOptionModel = add.value[idx] as FieldOptionModel;
    // Check if option starts with "option" get the integer that follows
    if (deletedValue.value.startsWith(Config.CONSTANTS.optionKeyDefaultPrefix)) {
      const delValueString = deletedValue.value.substring(Config.CONSTANTS.optionKeyDefaultPrefix.length);
      const delValueNumber = parseInt(delValueString, 10);
      if (delValueNumber === this.nextValue - 1) {
        // We are deleting the last value: decrease nextValue
        this.nextValue--;
      }
    }
    add.removeAt(idx);
  }

  onSaveOptions(ind) {
    const val = this.form.value;
    const options: FieldOptionModel[] = val.options as FieldOptionModel[];
    this.data.options = options;
  }

  getControls(): FormControl {
    return (this.form.get('options') as FormControl);
  }

  onClearAllOptions() {
    const add = this.form.get('options') as FormArray;
    while (add.length !== 0) {
      add.removeAt(0);
      this.nextValue--;
    }
  } 
 
  /**
   * Called when we 
   * @param modifiedTemplate Structure that contains a string template 
   *    along with conditional related properties
   */
  getModifiedTemplate(modifiedTemplate) {
    this.data = this.templateService.getModifiedTemplate(modifiedTemplate, this.data);
    // Trigger value change
    this.template = this.data.template;
  }

  onCommentsRequired(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      const selectedComments: any = this.form.controls['commentFieldOn'].value;
      this.data.commentFieldOn = selectedComments;
    } catch (error) {
      console.log("onCommentsRequired "+error);
    }
  }

  onChangeCommentKey(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.data.commentKey = this.form.controls['commentKey'].value as string;
    } catch (error) {
      console.log("commentKey "+error);
    }
  }

  onChangeCommentFieldOnOptional(value) {
    try {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.data.commentFieldOnOptional = this.form.controls['commentFieldOnOptional'].value as boolean;
    } catch (error) {
      console.log("commentFieldOnOptional "+error);
    }
  }

  /**
   * Initialization for conditional comment checkbox conversion
   */
  initCommentField(){
    let commentFieldArr = this.data.commentFieldOn?.split(";");
    this.data.options?.forEach(element => {
      this.commentFieldGroup.push({ label: element.label, value: element.value,
         checked: commentFieldArr && commentFieldArr.includes(element.value) ? true : false
        });
    });
  }

  /** 
   * Conditional comment checkbox state
   */
  changeCommentStatus(item,evnt,ind){
    item.checked = !item.checked;
    this.data.commentFieldOn = this.commentFieldGroup.filter((item)=> item.checked).map(({value}) => value).join(";");
  }
}
